import { useEffect, useMemo, useState } from "react";
import { t } from "i18next";
import isEmpty from "lodash/isEmpty";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useOutletContext } from "react-router";

import AddGuests from "@components/Booking/AddGuest/AddGuest";
import BookingCalendarDrawer from "@components/Booking/BookingCalendarDrawer/BookingCalendarDrawer";
import BookingCalendarToggle from "@components/Booking/BookingCalendarToggle/BookingCalendarToggle";
import BookingForm from "@components/Booking/BookingForm/BookingForm";
import DateStrip from "@components/Booking/DateStrip/DateStrip";
import ResourceSelector from "@components/Booking/ResourceSelector/ResourceSelector";
import TimeSelect from "@components/Booking/TimeSelect/TimeSelect";
import Loader from "@components/shared/Loader/Loader";
import WarningMessageBox from "@components/Unit/WarningMessageBox/WarningMessageBox";
import { supportEnum } from "@enums/route.enum";
import { useBooking } from "@hooks/useBooking/useBooking";
import { usePricing } from "@hooks/usePricing/usePricing";
import { BookingFetchResponseType, SingleSlot } from "@models/services/booking";
import { getLocationType, locationType } from "@models/services/organisation";
import { userProfile } from "@models/services/user";
import { ContactDetailsType } from "@models/shared/shared.model";
import authSlice from "@slices/auth.slice";
import bookingSlice from "@slices/booking.slice";
import bookingCalendarSlice from "@slices/bookingCalendar.slice";
import drawerSlice from "@slices/drawer.slice";
import { RootState } from "@store/index";

import modalSlice from "../../slices/modal.slice";

const OrganisationBooking = () => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state: RootState) => ({
    isAuthenticated: state.local.isAuthenticated,
  }));
  const showCalendarSection = useSelector(
    (State: RootState) => State.app.showCalendarSection
  );
  const { guestList, paymentLoader, selectedDate, selectedSlot, selectedTime } =
    useSelector((State: RootState) => State.booking);

  const navigate = useNavigate();
  const { cancelBooking, fetchSlots, getSelectedResource } = useBooking();

  const { pathname } = useLocation();
  const [guestError, setGuestError] = useState("");
  const [bookingSlots, setBookingSlots] = useState<BookingFetchResponseType>(
    {} as BookingFetchResponseType
  );

  const {
    fetchQuotaInfo,
    fetchResourcesPrices,
    priceLoader,
    syncQuotaWithBookingSlots,
  } = usePricing();

  const [isLoading, setIsLoading] = useState(false);

  const context: [
    getLocationType,
    locationType,
    userProfile,
    ContactDetailsType,
  ] = useOutletContext();

  const isArchived = context[0]?.isFgArchived || false;
  const locationId = context[1]?.id;

  const isFinanceConfigured = context[1]?.financeConfigured || false;

  useEffect(() => {
    if (!paymentLoader) {
      if (context[1] && locationId) {
        if (isAuthenticated) {
          setIsLoading(true);
          fetchSlots({
            bookingDate: selectedDate,
            locationId: locationId || "0",
          })
            .then(slots => {
              setBookingSlots(slots);

              setIsLoading(false);
            })
            .catch(() => {
              setIsLoading(false);
            });
        }
      }
    }
  }, [selectedDate, locationId, paymentLoader, isAuthenticated]);

  useEffect(() => {
    dispatch(bookingCalendarSlice.actions.setTempEvents([]));
  }, [selectedDate, locationId]);

  useEffect(() => {
    if (!showCalendarSection) {
      dispatch(drawerSlice.actions.setShowBookingDrawer(false));
      dispatch(bookingCalendarSlice.actions.setTempEvents([]));
    }
  }, [showCalendarSection]);

  const quoteInfoPayload = useMemo(() => {
    if (!selectedTime || !bookingSlots?.resources?.length) return null;

    return {
      bookingDate: selectedDate,
      locationId: bookingSlots?.locationId,
      selectedTime,
    };
  }, [selectedTime, selectedDate]);

  useEffect(() => {
    if (!quoteInfoPayload) return;

    const loadQuotaInfo = async () => {
      const quotaData = await fetchQuotaInfo(quoteInfoPayload);

      if (quotaData) {
        setBookingSlots(prevBookingSlots =>
          syncQuotaWithBookingSlots(quotaData, prevBookingSlots)
        );
      }
    };

    loadQuotaInfo();
  }, [quoteInfoPayload]);

  useEffect(() => {
    if (!isFinanceConfigured) {
      return;
    }
    fetchResourcesPrices(bookingSlots);
  }, [selectedTime, guestList, isFinanceConfigured]);

  useEffect(() => {
    dispatch(bookingSlice.actions.changeSelectedSlot({} as SingleSlot));
    dispatch(bookingSlice.actions.setGuests([]));

    return () => {
      dispatch(bookingSlice.actions.setPaymentLoader(false));
      cancelBooking();
    };
  }, []);

  useEffect(() => {
    setGuestError("");
  }, [guestList, selectedSlot]);

  const checkBookingDisable = () => {
    return isArchived || paymentLoader;
  };

  const selectedSlotInfo = useMemo(
    () => getSelectedResource(bookingSlots),
    [bookingSlots, getSelectedResource]
  );
  const allowBringGuest = Boolean(
    selectedSlotInfo?.policyInfo?.allowBringGuest &&
      selectedSlotInfo?.slotInfo?.slots?.allowedGuestTimings.length > 0
  );
  const loginClick = () => {
    if (pathname.includes(supportEnum.Auth)) {
      dispatch(
        authSlice.actions.passParams({ userEmail: "", verificationCode: "" })
      );
      navigate("/", { replace: true });
    }
    dispatch(modalSlice.actions.updateLoginModal(true));
  };
  return (
    <>
      {locationId && (
        <>
          <Loader isLoading={isLoading} />
          <div
            className="fg-tab-content-wrap fg-booking-section"
            data-testid="organisation-booking"
          >
            {checkBookingDisable() && (
              <div
                className="container mt-3"
                data-testid="booking-disable-banner"
              >
                {isArchived && (
                  <WarningMessageBox message={t("archivedContactWarningMsg")} />
                )}
              </div>
            )}
            <DateStrip isBookingDisable={checkBookingDisable()} />

            {isAuthenticated && bookingSlots?.showCalendar && (
              <BookingCalendarToggle isBookingDisable={checkBookingDisable()} />
            )}

            <div className="container">
              <div className="row">
                <div className="col-sm-12">
                  {!isAuthenticated ? (
                    <div className="alert alert-info small p-2 fg-booking-authenticated-banner">
                      {t("bookingInfoForNonLogin")}
                      <button
                        className="btn fg-btn fg-btn-lightblue fg-btn-booknow"
                        onClick={loginClick}
                      >
                        {t("login")}
                      </button>
                    </div>
                  ) : (
                    <>
                      {!isEmpty(bookingSlots) && (
                        <TimeSelect
                          checkBookingDisable={checkBookingDisable()}
                          slots={bookingSlots}
                          bookingLoader={isLoading}
                        />
                      )}

                      {!(showCalendarSection && bookingSlots?.showCalendar) && (
                        <div className="col-md-8">
                          {selectedTime !== "" &&
                            bookingSlots?.resources?.length > 0 && (
                              <ResourceSelector
                                isBookingDisable={checkBookingDisable()}
                                slots={bookingSlots}
                                unitTypeTerminology={
                                  context[1].resourceType?.terminology
                                }
                                priceLoader={priceLoader}
                              />
                            )}
                          {selectedTime !== "" &&
                            Object.keys(selectedSlot || {}).length > 0 &&
                            selectedSlot?.isBookable &&
                            !(!allowBringGuest && guestList.length === 0) && (
                              <AddGuests
                                slotInfo={bookingSlots}
                                guestError={guestError}
                              />
                            )}
                          {Object.keys(selectedSlot || {}).length ? (
                            <BookingForm
                              slots={bookingSlots}
                              urls={{
                                gdpr: context[1].gdprUrl,
                                gdprTerms: context[1]?.gdprTerms,
                                legal_terms: context[1].termsUrl,
                                termsService: context[1]?.termsService,
                              }}
                              setGuestError={setGuestError}
                            />
                          ) : null}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <BookingCalendarDrawer
            checkBookingDisable={checkBookingDisable}
            bookingSlots={bookingSlots}
            priceLoader={priceLoader}
          />
        </>
      )}
    </>
  );
};

export default OrganisationBooking;
